// src/features/incidents/incidentsIoT.js
import { getIncidents, INCIDENT_STATUS, INCIDENT_PRIORITY } from './incidentsService';

class IncidentsIoTMonitor {
  constructor() {
    this.sensors = new Map();
    this.alertThresholds = {
      temperature: { min: 15, max: 85 },
      vibration: { max: 120 },
      pressure: { min: 2, max: 8 },
      humidity: { max: 80 },
      power_consumption: { max: 15 }
    };
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.createdIncidentsToday = new Set();
    this.simulationInterval = null;
    this.anomalyCheckInterval = null;
  }

  // Підключення до IoT платформи
  async connectToPlatform(platformConfig = {}) {
    try {
      console.log('🔌 Підключення до IoT платформи...');
      
      // Симуляція підключення до MQTT брокера або REST API
      this.connected = await this.initializeConnection(platformConfig);
      
      if (this.connected) {
        console.log('✅ Успішно підключено до IoT платформи');
        this.reconnectAttempts = 0;
        this.startMonitoring();
      } else {
        throw new Error('Не вдалося підключитися до IoT платформи');
      }
    } catch (error) {
      console.error('❌ Помилка підключення до IoT:', error);
      await this.handleReconnection();
    }
  }

  // Ініціалізація з'єднання
  async initializeConnection(config) {
    // Симуляція затримки підключення
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Тут буде реальне підключення до:
    // - MQTT брокера (AWS IoT, Mosquitto)
    // - REST API платформи
    // - WebSocket з'єднання
    return true; // Симуляція успішного підключення
  }

  // Запуск моніторингу
  startMonitoring() {
    console.log('📡 Запуск IoT моніторингу...');
    
    // Симуляція отримання даних з датчиків
    this.simulationInterval = setInterval(() => {
      this.generateSensorData();
    }, 5000); // Оновлення кожні 5 секунд

    // Перевірка аномалій кожні 10 секунд
    this.anomalyCheckInterval = setInterval(() => {
      this.checkForAnomalies();
    }, 10000);
  }

  // Генерація тестових даних датчиків
  generateSensorData() {
    const machines = ['washer_1', 'washer_2', 'dryer_1', 'tunnel_1', 'boiler_1'];
    const sensorTypes = ['temperature', 'vibration', 'pressure', 'humidity', 'power_consumption'];

    machines.forEach(machineId => {
      if (!this.sensors.has(machineId)) {
        this.sensors.set(machineId, {});
      }

      const machineSensors = this.sensors.get(machineId);
      
      sensorTypes.forEach(sensorType => {
        const value = this.generateSensorValue(sensorType, machineId);
        const timestamp = new Date().toISOString();
        
        machineSensors[sensorType] = {
          value,
          timestamp,
          unit: this.getSensorUnit(sensorType),
          status: this.checkSensorStatus(sensorType, value)
        };

        // Логування критичних значень
        if (machineSensors[sensorType].status === 'critical') {
          console.warn(`🚨 КРИТИЧНИЙ ПОКАЗНИК: ${machineId} - ${sensorType}: ${value}${this.getSensorUnit(sensorType)}`);
        }
      });

      // Оновлення UI через подію (якщо потрібно)
      this.dispatchSensorUpdate(machineId, machineSensors);
    });
  }

  // Генерація значень для різних типів датчиків
  generateSensorValue(sensorType, machineId) {
    const baseValues = {
      temperature: () => 20 + Math.random() * 60, // 20-80°C
      vibration: () => Math.random() * 150, // 0-150 Hz
      pressure: () => 3 + Math.random() * 5, // 3-8 bar
      humidity: () => 30 + Math.random() * 50, // 30-80%
      power_consumption: () => 5 + Math.random() * 12 // 5-17 kW
    };

    let value = baseValues[sensorType]();

    // Додавання аномалій для тестування
    if (Math.random() < 0.05) { // 5% шанс аномалії
      value = this.generateAnomaly(sensorType, value);
    }

    return Math.round(value * 10) / 10;
  }

  // Генерація аномальних значень
  generateAnomaly(sensorType, baseValue) {
    const anomalies = {
      temperature: baseValue * 1.8, // Перегрів
      vibration: baseValue * 3, // Сильна вібрація
      pressure: baseValue * 0.3, // Падіння тиску
      humidity: 95, // Дуже висока вологість
      power_consumption: baseValue * 2.5 // Стрибок споживання
    };

    return anomalies[sensorType] || baseValue;
  }

  // Перевірка статусу датчика
  checkSensorStatus(sensorType, value) {
    const threshold = this.alertThresholds[sensorType];
    if (!threshold) return 'normal';

    if (sensorType === 'temperature') {
      if (value < threshold.min || value > threshold.max) return 'critical';
      if (value > threshold.max * 0.9 || value < threshold.min * 1.1) return 'warning';
    } else {
      if (value > threshold.max) return 'critical';
      if (value > threshold.max * 0.8) return 'warning';
    }

    return 'normal';
  }

  // Отримання одиниць виміру
  getSensorUnit(sensorType) {
    const units = {
      temperature: '°C',
      vibration: 'Hz',
      pressure: 'bar',
      humidity: '%',
      power_consumption: 'kW'
    };
    return units[sensorType] || '';
  }

  // Перевірка аномалій та створення інцидентів
  async checkForAnomalies() {
    for (const [machineId, sensors] of this.sensors) {
      for (const [sensorType, data] of Object.entries(sensors)) {
        if (data.status === 'critical') {
          await this.createIncidentFromAnomaly(machineId, sensorType, data);
        }
      }
    }
  }

  // Створення інциденту з аномалії
  async createIncidentFromAnomaly(machineId, sensorType, sensorData) {
    const incidentKey = `${machineId}_${sensorType}_${sensorData.timestamp.split('T')[0]}`;
    
    // Перевірка, чи вже створено інцидент сьогодні для цього датчика
    if (this.createdIncidentsToday.has(incidentKey)) {
      return;
    }

    const incidentData = {
      date: new Date().toISOString(),
      type: 'technical',
      clientId: 'system',
      clientName: 'IoT Monitoring System',
      description: this.generateIncidentDescription(machineId, sensorType, sensorData),
      responsible: 'IoT System',
      status: INCIDENT_STATUS.OPEN,
      priority: INCIDENT_PRIORITY.HIGH,
      photos: [],
      iotData: {
        machineId,
        sensorType,
        sensorValue: sensorData.value,
        sensorUnit: sensorData.unit,
        threshold: this.alertThresholds[sensorType],
        timestamp: sensorData.timestamp
      },
      createdBy: {
        id: 'iot_system',
        name: 'IoT Monitoring',
        role: 'system'
      }
    };

    try {
      // Використовуємо існуючий сервіс для створення інциденту
      const incidents = await getIncidents();
      const newIncident = {
        id: 'iot_' + Date.now(),
        createdAt: new Date().toISOString(),
        ...incidentData
      };

      incidents.unshift(newIncident);
      await this.saveIncidents(incidents);
      
      this.createdIncidentsToday.add(incidentKey);
      
      console.log(`🆕 Створено IoT інцидент: ${machineId} - ${sensorType}`);
      
      // Відправка сповіщення
      this.sendIoTAlert(incidentData);
      
    } catch (error) {
      console.error('❌ Помилка створення IoT інциденту:', error);
    }
  }

  // Генерація опису інциденту
  generateIncidentDescription(machineId, sensorType, sensorData) {
    const descriptions = {
      temperature: `Перевищення температури на обладнанні ${machineId}. Поточне значення: ${sensorData.value}°C при допустимому діапазоні ${this.alertThresholds.temperature.min}-${this.alertThresholds.temperature.max}°C`,
      vibration: `Підвищена вібрація на обладнанні ${machineId}. Поточне значення: ${sensorData.value}Hz при максимально допустимому ${this.alertThresholds.vibration.max}Hz`,
      pressure: `Відхилення тиску на обладнанні ${machineId}. Поточне значення: ${sensorData.value}bar при допустимому діапазоні ${this.alertThresholds.pressure.min}-${this.alertThresholds.pressure.max}bar`,
      humidity: `Підвищена вологість на обладнанні ${machineId}. Поточне значення: ${sensorData.value}% при максимально допустимому ${this.alertThresholds.humidity.max}%`,
      power_consumption: `Підвищене споживання енергії на обладнанні ${machineId}. Поточне значення: ${sensorData.value}kW при максимально допустимому ${this.alertThresholds.power_consumption.max}kW`
    };

    return descriptions[sensorType] || `Аномалія датчика ${sensorType} на обладнанні ${machineId}. Значення: ${sensorData.value}${sensorData.unit}`;
  }

  // Відправка IoT сповіщення
  sendIoTAlert(incidentData) {
    if (window.notificationActions) {
      window.notificationActions.addNotification({
        type: 'iot_alert',
        priority: 'high',
        title: '🚨 IoT Аномалія Виявлена',
        message: `Обладнання: ${incidentData.iotData.machineId}\nДатчик: ${incidentData.iotData.sensorType}\nЗначення: ${incidentData.iotData.sensorValue}${incidentData.iotData.sensorUnit}`,
        data: incidentData,
        category: 'iot_alerts'
      });
    }

    // Відправка через інтеграції
    if (window.incidentIntegrations) {
      const channels = {
        slack: { channel: '#iot-alerts' },
        email: { 
          recipients: ['technicians@lainsa.com'],
          template: 'urgent'
        }
      };
      window.incidentIntegrations.broadcastIncident(incidentData, channels);
    }
  }

  // Отримання даних конкретного датчика
  getSensorData(machineId, sensorType) {
    const machineSensors = this.sensors.get(machineId);
    return machineSensors ? machineSensors[sensorType] : null;
  }

  // Отримання всіх даних машини
  getMachineData(machineId) {
    return this.sensors.get(machineId) || {};
  }

  // Отримання всіх даних
  getAllSensorData() {
    return Object.fromEntries(this.sensors);
  }

  // Оновлення порогових значень
  updateThresholds(newThresholds) {
    this.alertThresholds = { ...this.alertThresholds, ...newThresholds };
    console.log('📊 Оновлено порогові значення:', this.alertThresholds);
  }

  // Зупинка моніторингу
  stopMonitoring() {
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
    }
    if (this.anomalyCheckInterval) {
      clearInterval(this.anomalyCheckInterval);
    }
    this.connected = false;
    console.log('⏹️ IoT моніторинг зупинено');
  }

  // Обробка перепідключення
  async handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 Спроба перепідключення ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
      
      await new Promise(resolve => setTimeout(resolve, 5000));
      await this.connectToPlatform();
    } else {
      console.error('❌ Досягнуто максимальну кількість спроб перепідключення');
    }
  }

  // Відправка оновлення через подію
  dispatchSensorUpdate(machineId, sensors) {
    const event = new CustomEvent('iotSensorUpdate', {
      detail: { machineId, sensors, timestamp: new Date().toISOString() }
    });
    window.dispatchEvent(event);
  }

  // Тимчасовий метод для збереження (поки не маємо доступу до saveIncidents)
  async saveIncidents(incidents) {
    try {
      // Використовуємо storageService напряму
      const { saveData } = await import('../../services/storageService');
      await saveData('lainsa.incidents', incidents);
    } catch (error) {
      console.error('Помилка збереження інцидентів:', error);
    }
  }
}

// Експорт синглтону
export const incidentsIoTMonitor = new IncidentsIoTMonitor();
export default incidentsIoTMonitor;